import { Component, inject, OnInit, HostListener, AfterViewInit, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { EventViewModel } from '../../../models-views/event.view.model';
import { AppCommon } from '../../../providers/app-common';
import LoginModel from '../../../models/user.model';
import { DatePipe } from '@angular/common'; // Import DatePipe
import { environment } from '../../../../environments/environment';

// Interface for the structured row data for the table
interface TransformedRow {
  featureLabel: string;
  dataKey: string; // The ID of the question or a custom key for pricing
  isGroupTitle?: boolean;
  // Added 'currency', 'tax-array', 'file-array' for rendering hints
  dataType?: 'string' | 'boolean' | 'pax' | 'notes' | 'number' | 'date' | 'multi-choice' | 'single-choice' | 'file-upload' | 'currency' | 'tax-array' | 'file-array';
  originalQuestionType?: string; // NEW: Store the original question type for direct switching in HTML
}

// Interface for transformed quotation data
interface TransformedQuotation {
  submission_id: number;
  propertyName: string;
  propertyType: string;
  answersMap: { [questionId: number]: any }; // Map question ID to its answer
  quotation_request_vendor: string;
  isFinalized: boolean;
  isShortlisted: boolean;
  vendor: number;
  access_token: string;
  quotation_request: number;
  // New pricing properties directly from selectedItems
  currency?: string;
  exchangeRate?: number;
  totalPrice?: number;
  usdAmount?: number;
  taxes?: { id: number; name: string; value: number; url?: string }[]; // Corresponds to item.tax
  additionalFiles?: { id: number; name: string; url: string }[]; // Corresponds to item.file
  amountBeforeTax?: number; // This needs to be calculated or directly available
}

interface IModalData {
  eventId: any;
  selectedItems: any;
}

@Component({
  selector: 'app-compare-quotations',
  standalone: false,
  templateUrl: './compare-quotations.component.html',
  styleUrl: './compare-quotations.component.scss',
  providers: [DatePipe] // Provide DatePipe here if not provided at a higher level
})
export class CompareQuotationsComponent implements OnInit, AfterViewInit, OnDestroy { // Implement AfterViewInit, OnDestroy

  readonly nzModalData: IModalData = inject(NZ_MODAL_DATA);
  loginModel: any

  quotations: TransformedQuotation[] = [];
  transformedData: TransformedRow[] = [];
  scrollX: string = '1500px';
  // Change scrollY to a dynamic value or 'auto'
  scrollY: string = 'auto'; // Will be dynamically calculated

  fixedColumnWidth: string = '220px';
  quotationColumnWidth: string = '280px';
  adjusttableHightDelay: number = 100;

  isLoading = false;

  selectedItems: any[] = [];
  eventId: any;
  dataList: any[] = [];

  private pendingApiCallsCount: { [key: number]: number } = {};

  // Reference to the table container for height calculation
  @ViewChild('comparisonTableContainer') comparisonTableContainer!: ElementRef;

  // Inject DatePipe, ElementRef, and ChangeDetectorRef
  constructor(
    private modalRef: NzModalRef,
    private vm: EventViewModel,
    private datePipe: DatePipe,
    private el: ElementRef, // Inject ElementRef to access the component's host element
    private cdr: ChangeDetectorRef // Inject ChangeDetectorRef for manual UI updates
  ) {
    this.loginModel = AppCommon.getloggedInObject();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.selectedItems = JSON.parse(JSON.stringify(this.nzModalData.selectedItems));
    this.eventId = this.nzModalData.eventId;
    this.vm.parentCompareQuotationsComponent = this;
    this.initData();
  }

  ngAfterViewInit(): void {
    // Adjust height after the view has been initialized and rendered
    // This is crucial for initial load to ensure elements are in the DOM.
    setTimeout(() => {
      this.adjustTableHeight();
    }, this.adjusttableHightDelay);
  }

  ngOnDestroy(): void {
    // Good practice for cleanup if any manual event listeners were attached,
    // though @HostListener cleans itself up.
  }

  // Listen for window resize events to re-adjust table height
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    setTimeout(() => {
      this.adjustTableHeight();
    }, this.adjusttableHightDelay);
  }

  /**
   * Dynamically calculates and sets the scrollY property for the nz-table.
   * This ensures the table body takes up the remaining available height
   * within its container, preventing extra space.
   */
  private adjustTableHeight(): void {
    // Select the table container and the table itself
    // Use `el.nativeElement` to query within this component's host element
    const container = this.el.nativeElement.querySelector('.comparison-table-container') as HTMLElement;
    const table = this.el.nativeElement.querySelector('.comparison-nz-table') as HTMLElement;

  

    if (container && table) {
      // Get the client height of the container that holds the table
      const containerHeight = container.clientHeight;
     

      // Get the height of the table header (thead)
      const header = table.querySelector('thead');
      const headerHeight = header ? header.clientHeight : 0;
    

      // Get the height of the table footer (tfoot)
      const footer = table.querySelector('tfoot');
      const footerHeight = footer ? footer.clientHeight : 0;
    
      // Calculate the available height for the scrollable table body
      // We subtract a small buffer (e.g., 2px) to account for potential borders or
      // rounding issues that might cause an unnecessary scrollbar if the calculation is exact.
      const availableBodyHeight = containerHeight + 60 - headerHeight - footerHeight - 2; // Subtract a small buffer
    

      // Ensure a minimum height to prevent issues if calculated height is too small
      this.scrollY = `${Math.max(availableBodyHeight, 100)}px`; // Minimum height of 100px
    } else {
      // Fallback if elements are not found (e.g., during initial render before data)
      // You can set a default scrollY or just let it remain 'auto'
      this.scrollY = '78vh'; // Or a suitable default
    }
   
  }

  initData(): void {
    if (this.selectedItems.length === 0) {
      this.allApiCallsFinished();
      return;
    }

    this.selectedItems.forEach((item, index) => {
      const formId = item.quotation_request_vendor_details.quotation_request_details.outsourcing_form;
      const submissionId = item.id;
      const trackingId = index;
      const quotation_request_vendor = item.quotation_request_vendor;

      const newItem = {
        form_id: formId,
        submission_id: submissionId,
        quotation_request_vendor: quotation_request_vendor,
        isFinalized: item.quotation_request_vendor_details.is_finalized,
        isShortlisted: item.quotation_request_vendor_details.is_shortlisted,
        sections: [],
        questions: [],
        answers: [],
        data_received: {
          sections: false,
          questions: false,
          answers: false
        },
        tracking_id: trackingId
      };
      this.dataList.push(newItem);

      this.pendingApiCallsCount[trackingId] = 0;

      this.pendingApiCallsCount[trackingId]++;
      this.vm.getVendorAnswars(1, 100, submissionId, 'compare-quotations');

      this.pendingApiCallsCount[trackingId]++;
      this.vm.getSections(formId, 'compare-quotations', trackingId);
    });
  }

  sectionsDataReceived(response: any, trackingId: number): void {

    const item = this.dataList[trackingId];
    const results = response.results || [];
    item.sections = results;
    item.data_received.sections = true;

    if (item.sections.length > 0) {
      this.pendingApiCallsCount[trackingId]++;
      this.vm.getOSQuestions(item.sections.map((el: any) => el.id).join(','), 'compare-quotations', trackingId);
    } else {
      item.data_received.questions = true;
    }

    this.checkAndCallAllFinished(trackingId);
  }

  questionsDataReceived(response: any, trackingId: number): void {

    const item = this.dataList[trackingId];
    const results = response.results || [];
    item.questions = results;
    item.data_received.questions = true;
    this.checkAndCallAllFinished(trackingId);
  }

  vendorAnswersRecieved(response: any, submission_id: number): void {

    const item = this.dataList.find(el => el.submission_id === submission_id);
    if (item) {
      const trackingId = item.tracking_id;
      const results = response.results || [];
      results.forEach((element: any) => {
        if (element.submission_details) delete element.submission_details;
        if (element.question_details) delete element.question_details;
      });
      item.answers = results;
      item.data_received.answers = true;
      this.checkAndCallAllFinished(trackingId);
    }
  }

  private checkAndCallAllFinished(trackingId: number): void {
    if (this.pendingApiCallsCount[trackingId] > 0) {
      this.pendingApiCallsCount[trackingId]--;
    }

    const item = this.dataList[trackingId];
    if (item && item.data_received.sections && item.data_received.questions && item.data_received.answers) {
      const allItemsProcessed = this.dataList.every(dataItem =>
        dataItem.data_received.sections &&
        dataItem.data_received.questions &&
        dataItem.data_received.answers
      );

      if (allItemsProcessed) {
        this.allApiCallsFinished();
      }
    }
  }

  private allApiCallsFinished(): void {
    this.isLoading = false;
    this.prepareQuotationsAndFeatures();
    this.updateScrollX();
    // After data is loaded and table is rendered, adjust height
    setTimeout(() => {
      this.adjustTableHeight();
    }, this.adjusttableHightDelay);

  }

  private prepareQuotationsAndFeatures(): void {
    const uniqueQuestionsMap: { [questionId: number]: any } = {};
    const processedSectionIds = new Set<number>();

    this.quotations = this.dataList.map(dataItem => {
      const answersMap: { [questionId: number]: any } = {};
      dataItem.answers.forEach((answer: any) => {
        const questionId = answer.question;
        let value: any;

        const questionDetails = dataItem.questions.find((q: any) => q.id === questionId);

        switch (questionDetails?.question_type) {
          case 'TEXT_SHORT':
            value = answer.answer_text_short;
            break;
          case 'TEXT_LONG':
            value = answer.answer_text_long;
            break;
          case 'NUMBER':
            value = answer.answer_number !== null ? parseFloat(answer.answer_number) : null;
            break;
          case 'DATE':
            value = answer.answer_date; // Keep as string for DatePipe to process
            break;
          case 'MULTIPLE_CHOICE_SINGLE':
          case 'DROPDOWN':
            const selectedChoice = questionDetails?.choices?.find((choice: any) => choice.id === answer.answer_selected_choice_ids);
            value = selectedChoice?.choice_text || answer.answer_selected_choice_ids || null;
            break;
          case 'MULTIPLE_CHOICE_MULTI':
            // Changed: Now 'value' will be an array of selected choice texts
            value = (answer.answer_selected_choice_ids || [])
              .map((id: number) => questionDetails?.choices?.find((choice: any) => choice.id === id)?.choice_text)
              .filter(Boolean);
            break;
          case 'BOOLEAN': // Using 'BOOLEAN' as per TS logic
            value = answer.answer_boolean;
            break;
          case 'FILE_UPLOAD':
            // Now 'value' will be an array of file references (URLs)
            value = answer.answer_file_references || null;
            break;
          case 'MULTIPLE_FILES':
            // Now 'value' will be an array of file references (URLs)
            value = answer.answer_multiple_files || null;
            break;
          case 'CURRENCY': // Added CURRENCY case as it exists in HTML with a pipe
            value = answer.answer_number !== null ? parseFloat(answer.answer_number) : null;
            break;
          default:
            value = 'N/A';
        }
        answersMap[questionId] = value;
      });

      const correspondingSelectedItem = this.selectedItems.find(sItem => sItem.id === dataItem.submission_id);
      const propertyName = correspondingSelectedItem?.quotation_request_vendor_details?.vendor_details?.name || 'N/A';
      const propertyType = correspondingSelectedItem?.quotation_request_vendor_details?.quotation_request_details?.request_title || 'N/A';

      // Extract pricing details directly from selectedItems
      const currency = correspondingSelectedItem?.currency || 'N/A';
      const exchangeRate = correspondingSelectedItem?.exchange_rate !== null ? parseFloat(correspondingSelectedItem?.exchange_rate) : null;
      const totalPrice = correspondingSelectedItem?.total_price !== null ? parseFloat(correspondingSelectedItem?.total_price) : null;
      const usdAmount = correspondingSelectedItem?.usd_amount !== null ? parseFloat(correspondingSelectedItem?.usd_amount) : null;
      // Ensure these are arrays and map them to expected structure if needed.
      // item.tax: [id, name, url] - assuming this means array of objects like {id, name, value}
      const taxes = correspondingSelectedItem?.tax?.map((t: any) => ({
        id: t.id,
        name: t.name,
        value: t.value || 0 // Assuming 'value' for tax amount
      })) || [];
      // item.file_details: [id, file_url, name] - array of objects with file metadata
      const additionalFiles = (correspondingSelectedItem?.file_details || [])
        .filter((f: any) => f && (f.url || f.file_url || f.name || f.file_name))
        .map((f: any) => ({
          id: f.id,
          name: f.name || f.file_name,
          url: this.getFullFileUrl(f.url || f.file_url),
        }));

      // If amount_before_tax is not directly available, you might calculate it
      // For now, assuming it's directly available as per your previous message context
      const amountBeforeTax = correspondingSelectedItem?.amount_before_tax !== null ? parseFloat(correspondingSelectedItem?.amount_before_tax) : null;
      const vendor = correspondingSelectedItem?.quotation_request_vendor_details?.vendor;
      const access_token = correspondingSelectedItem?.quotation_request_vendor_details?.access_token;
      const quotation_request = correspondingSelectedItem?.quotation_request_vendor_details?.quotation_request;

      return {
        submission_id: dataItem.submission_id,
        propertyName: propertyName,
        propertyType: propertyType,
        answersMap: answersMap,
        isFinalized: dataItem.isFinalized,
        isShortlisted: dataItem.isShortlisted,
        vendor:vendor,
        access_token:access_token,
        quotation_request:quotation_request,
        quotation_request_vendor: dataItem.quotation_request_vendor,
        currency: currency,
        exchangeRate: exchangeRate,
        totalPrice: totalPrice,
        usdAmount: usdAmount,
        taxes: taxes,
        additionalFiles: additionalFiles,
        amountBeforeTax: amountBeforeTax // Assign the fetched value
      };
    });

    this.dataList.forEach(dataItem => {
      dataItem.sections.sort((a: any, b: any) => a.display_order - b.display_order);
      dataItem.questions.sort((a: any, b: any) => a.display_order - b.display_order);
    });

    if (this.dataList.length > 0) {
      const primaryForm = this.dataList[0];
      const allSections = primaryForm.sections;

      this.dataList.forEach(dataItem => {
        dataItem.questions.forEach((question: any) => {
          if (!uniqueQuestionsMap[question.id]) {
            uniqueQuestionsMap[question.id] = question;
          }
        });
      });

      allSections.forEach((section: any) => {
        if (!processedSectionIds.has(section.id)) {
          this.transformedData.push({
            isGroupTitle: true,
            featureLabel: section.section_title,
            dataKey: '',
            dataType: 'string',
            originalQuestionType: 'GROUP_TITLE'
          });
          processedSectionIds.add(section.id);
        }

        const sectionQuestions = Object.values(uniqueQuestionsMap)
          .filter((question: any) => question.section === section.id)
          .sort((a: any, b: any) => a.display_order - b.display_order);

        sectionQuestions.forEach((question: any) => {
          this.transformedData.push({
            featureLabel: question.question_text,
            dataKey: question.id.toString(),
            dataType: this.mapQuestionTypeToDataType(question.question_type),
            originalQuestionType: question.question_type
          });
        });
      });

      // --- Custom Pricing Section ---
      this.transformedData.push({
        isGroupTitle: true,
        featureLabel: 'Pricing Details',
        dataKey: '',
        dataType: 'string',
        originalQuestionType: 'GROUP_TITLE'
      });
      this.transformedData.push({
        featureLabel: 'Currency Type',
        dataKey: 'currency', // Key for retrieving from TransformedQuotation
        dataType: 'string',
        originalQuestionType: 'CUSTOM_CURRENCY'
      });
      this.transformedData.push({
        featureLabel: 'Amount Before Tax',
        dataKey: 'amountBeforeTax',
        dataType: 'number',
        originalQuestionType: 'CUSTOM_AMOUNT_BEFORE_TAX'
      });
      this.transformedData.push({
        featureLabel: 'Taxes',
        dataKey: 'taxes',
        dataType: 'tax-array', // Custom data type for rendering array of taxes
        originalQuestionType: 'CUSTOM_TAXES'
      });
      this.transformedData.push({
        featureLabel: 'Total Price',
        dataKey: 'totalPrice',
        dataType: 'number',
        originalQuestionType: 'CUSTOM_TOTAL_PRICE'
      });
      this.transformedData.push({
        featureLabel: 'Exchange Rate',
        dataKey: 'exchangeRate',
        dataType: 'number',
        originalQuestionType: 'CUSTOM_EXCHANGE_RATE'
      });
      this.transformedData.push({
        featureLabel: 'Amount in USD',
        dataKey: 'usdAmount',
        dataType: 'number',
        originalQuestionType: 'CUSTOM_USD_AMOUNT'
      });
      this.transformedData.push({
        featureLabel: 'Additional Files',
        dataKey: 'additionalFiles',
        dataType: 'file-array', // Custom data type for rendering array of files
        originalQuestionType: 'CUSTOM_ADDITIONAL_FILES'
      });
      // --- End Custom Pricing Section ---
    }

  }

  private mapQuestionTypeToDataType(questionType: string): 'string' | 'boolean' | 'pax' | 'notes' | 'number' | 'date' | 'multi-choice' | 'single-choice' | 'file-upload' | 'currency' | 'tax-array' | 'file-array' {
    switch (questionType) {
      case 'TEXT_SHORT':
        return 'string';
      case 'TEXT_LONG':
        return 'notes';
      case 'NUMBER':
      case 'CURRENCY': // Handle currency as a number type for general display
        return 'number';
      case 'DATE':
        return 'date';
      case 'BOOLEAN':
        return 'boolean';
      case 'MULTIPLE_CHOICE_SINGLE':
      case 'DROPDOWN':
        return 'single-choice';
      case 'MULTIPLE_CHOICE_MULTI':
        return 'multi-choice';
      case 'FILE_UPLOAD':
        return 'file-upload';
      // Custom data types for pricing section
      case 'CUSTOM_CURRENCY':
        return 'currency';
      case 'CUSTOM_AMOUNT_BEFORE_TAX':
        return 'number'; // It's a number, but a custom type for rendering
      case 'CUSTOM_TAXES':
        return 'tax-array';
      case 'CUSTOM_TOTAL_PRICE':
        return 'number';
      case 'CUSTOM_EXCHANGE_RATE':
        return 'number';
      case 'CUSTOM_USD_AMOUNT':
        return 'number';
      case 'CUSTOM_ADDITIONAL_FILES':
        return 'file-array';
      // Default case for any unhandled question types
      default:
        return 'string';
    }
  }

  getValue(quotation: TransformedQuotation, dataKey: string): any {
    if (!quotation) {
      return undefined;
    }

    // Check for custom pricing keys first
    switch (dataKey) {
      case 'currency':
        return quotation.currency;
      case 'exchangeRate':
        return quotation.exchangeRate;
      case 'totalPrice':
        return quotation.totalPrice;
      case 'usdAmount':
        return quotation.usdAmount;
      case 'taxes':
        return quotation.taxes;
      case 'additionalFiles':
        return quotation.additionalFiles;
      case 'amountBeforeTax':
        return quotation.amountBeforeTax;
      default:
        // If not a custom pricing key, treat as a question ID from answersMap
        const questionId = parseInt(dataKey, 10);
        return quotation.answersMap[questionId];
    }
  }

  markAsShortlisted(item: any): void {
    const payload = {
      quotation_request: item.quotation_request,
      is_shortlisted: !item.isShortlisted,
      modified_by: this.loginModel.user.id,
      vendor:item.vendor,
      access_token:item.access_token
    }
    this.vm.updateOutsourcingRequestVendor(payload, item.quotation_request_vendor,'Shortlisted', 'compare-quotations');
  }

  markAsFinalized(item: any): void {
    // Find the current state from the quotations array to ensure we have the correct value
    const currentItem = this.quotations.find(q => q.quotation_request_vendor === item.quotation_request_vendor);
    if (!currentItem) return;

    // Capture the ORIGINAL state before any modifications
    const currentFinalizedState = currentItem.isFinalized;
    const newFinalizedState = !currentFinalizedState;

    if (newFinalizedState) {
      // If finalizing this item (setting to true), make API calls to set ALL OTHER items to false
      this.quotations.forEach(q => {
        if (q.quotation_request_vendor !== item.quotation_request_vendor) {
          const otherPayload = {
            quotation_request: q.quotation_request,
            is_finalized: false,
            modified_by: this.loginModel.user.id,
            vendor: q.vendor,
            access_token: q.access_token
          }
          this.vm.updateOutsourcingRequestVendor(otherPayload, q.quotation_request_vendor, 'Finalized', 'compare-quotations');
        }
      });
    }

    // Call API for the clicked item with the correct new state
    const payload = {
      quotation_request: item.quotation_request,
      is_finalized: newFinalizedState,
      modified_by: this.loginModel.user.id,
      vendor: item.vendor,
      access_token: item.access_token
    }
    this.vm.updateOutsourcingRequestVendor(payload, item.quotation_request_vendor, 'Finalized', 'compare-quotations');
  }

  vendorSubmissionUpdated(type: string, payload: any) {

    const { vendor, is_shortlisted, is_finalized, quotation_request } = payload;
    
    // Match using vendor ID and quotation_request since we have both in the payload
    const item = this.quotations.find(q => 
      q.vendor === vendor && q.quotation_request === quotation_request
    );
    
    if (!item) {
      return;
    }

    switch (type) {
      case 'Shortlisted':
        item.isShortlisted = is_shortlisted;
        break;

      case 'Finalized':
        // Simply update this specific item's state from the API response
        item.isFinalized = is_finalized;
        break;
    }
  
    // Manually trigger change detection to update button UI immediately
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.adjustTableHeight();
    }, this.adjusttableHightDelay);
  }

  updateScrollX(): void {
    const dynamicWidth = parseInt(this.fixedColumnWidth, 10) + (this.quotations.length * parseInt(this.quotationColumnWidth, 10));
    this.scrollX = `${dynamicWidth + 20}px`;
  }

  getYesNoTagType(value: boolean): 'success' | 'error' | 'default' {
    if (value === undefined || value === null) return 'default';
    return value ? 'success' : 'error';
  }

  // Use DatePipe to format dates
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    // You can customize the format string as needed, e.g., 'shortDate', 'MM/dd/yyyy'
    return this.datePipe.transform(dateString, 'dd-MM-yyyy') || 'N/A';
  }

  // Helper to extract file name from URL (assuming a simple URL format)
  getFileNameFromUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      const parts = urlObj.pathname.split('/');
      return parts[parts.length - 1];
    } catch (e) {
    
      return 'Unknown File';
    }
  }

  // Helper to construct full file URL (prepend API base URL if relative path)
  getFullFileUrl(fileUrl: string): string {
    if (!fileUrl) return '';
    // If it's already a full URL, upgrade http:// to https:// to avoid Mixed Content errors
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl.replace(/^http:\/\//i, 'https://');
    }
    // If it's a relative path, prepend the API base URL
    return environment.api_url + fileUrl;
  }

  close(): void {
    this.modalRef.destroy();
  }

  downloadJsonFile(data: any, filename: string): void {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  getVendorIcon(firstLatter: string) {
    return AppCommon.getDicebearIcon(firstLatter);
  }

  // Utility to check if a file URL is an image
  isImageFile(url: string): boolean {
    if (!url) return false;
    const imageExtensions = /\.(png|jpg|jpeg|gif|bmp|webp|svg)$/i;
    return imageExtensions.test(url);
  }

}
